import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { fridgeCheckout } from '../../services/api';
import toast from 'react-hot-toast';

const FridgeCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('upi');

  const { fridgeId, fridgeName, apartment } = location.state || {};

  const handlePayment = async () => {
    setLoading(true);
    try {
      const orderData = {
        fridge_id: fridgeId,
        total_amount: getCartTotal(),
        payment_method: paymentMethod,
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantity || 1,
          price: item.price
        }))
      };

      const response = await fridgeCheckout(orderData);
      
      if (response.data.success) {
        clearCart();
        navigate('/payment-success', {
          state: {
            orderNumber: response.data.order_number,
            amount: getCartTotal()
          }
        });
        toast.success('Payment successful! Take your products');
      }
    } catch (error) {
      toast.error('Payment failed. Please try again');
    } finally {
      setLoading(false);
    }
  };

  if (!fridgeId) {
    navigate('/scan');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">Checkout</h1>
          
          {/* Fridge Info */}
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-gray-600">Fridge Location</p>
            <p className="font-semibold">{apartment} - {fridgeName}</p>
          </div>

          {/* Order Summary */}
          <div className="border-b pb-4 mb-4">
            <h2 className="font-semibold mb-3">Order Summary</h2>
            {cart.map((item, index) => (
              <div key={index} className="flex justify-between mb-2">
                <span>{item.name} x {item.quantity || 1}</span>
                <span className="font-medium">₹{item.price * (item.quantity || 1)}</span>
              </div>
            ))}
          </div>

          {/* Payment Method */}
          <div className="mb-6">
            <h2 className="font-semibold mb-3">Payment Method</h2>
            <div className="space-y-2">
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="payment"
                  value="upi"
                  checked={paymentMethod === 'upi'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <div>
                  <span className="font-medium">UPI</span>
                  <p className="text-sm text-gray-500">Google Pay, PhonePe, Paytm</p>
                </div>
              </label>
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="payment"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <div>
                  <span className="font-medium">Card</span>
                  <p className="text-sm text-gray-500">Credit/Debit Card</p>
                </div>
              </label>
            </div>
          </div>

          {/* Total Amount */}
          <div className="border-t pt-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">Total Amount</span>
              <span className="text-2xl font-bold text-blue-600">
                ₹{getCartTotal()}
              </span>
            </div>
          </div>

          {/* Pay Button */}
          <button
            onClick={handlePayment}
            disabled={loading}
            className={`w-full py-3 px-4 rounded-lg text-white font-medium
              ${loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700'}`}
          >
            {loading ? 'Processing...' : `Pay ₹${getCartTotal()}`}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            By proceeding, you agree to take the products from the fridge after payment
          </p>
        </div>
      </div>
    </div>
  );
};

export default FridgeCheckout;